<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useClipboard } from "@vueuse/core"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import { CONSENT_REQUEST_TYPE } from "@core/constants"

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant unique de la demande de partage du consentement */
        consentRequestId: string
        /** Type de la demande (SHARE pour partage, SIGN pour signature) */
        consentRequestType?: string
        /** Lien de la demande pour copie (pour SHARE : lien avec ?action=accepted) */
        link?: string
        /** Demande multi-compteurs (utilise consentrequestmulti pour send/preview) */
        multiple?: boolean
        /** Si true, envoie une relance ; sinon envoie l'invitation */
        sendBack?: boolean
    }>(),
    {
        consentRequestType: CONSENT_REQUEST_TYPE.SHARE,
        link: "",
        multiple: false,
        sendBack: false,
    }
)

const emit = defineEmits<{
    /** Fermer le formulaire (bouton Terminer, comme SourceInvitationSignSent) */
    done: []
    /** Après envoi réussi de l’e-mail d’invitation / relance */
    sent: []
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Demande de partage de données créée",
            help: "Votre demande a bien été créée auprès de nos services. Vous pouvez dès à présent envoyer votre demande par email ou bien en copiant le lien.",
            notif: "Nous vous notifierons par email dès que la demande sera acceptée par le titulaire",
            see: "Voir l'aperçu",
            copy: "Copier le lien de la demande",
            send: "Envoyer",
            quit: "Terminer",
            next: "Continuer",
            success:
                "Demande envoyée ! Vous pouvez suivre la demande sur la page compteur du bâtiment.",
        },
    },
})

// ================================
// STATE
// ================================

/** Prévisualisation HTML du mail */
const previewHtml = ref<string | null>(null)
/** Message de succès après envoi de l'email */
const successMessage = ref("")
/** Lien copié (feedback visuel) */
const copyDone = ref(false)

const { copy } = useClipboard()

// ================================
// FETCH WRAPPERS
// ================================

async function sendInvitationFn(): Promise<void> {
    if (props.multiple) {
        await consentRequestService.sendConsentRequestMulti(
            props.consentRequestId,
            props.sendBack
        )
    } else {
        await consentRequestService.sendConsentRequest(
            props.consentRequestId,
            props.sendBack
        )
    }
    successMessage.value = t("success")
    emit("sent")
}

const {
    fetch: fetchSend,
    isFetching: isSending,
    hasError: hasSendError,
    error: sendError,
} = useFetchWrapper(sendInvitationFn)

async function previewInvitationFn(): Promise<void> {
    const html = props.multiple
        ? await consentRequestService.previewConsentRequestMulti(
              props.consentRequestId
          )
        : await consentRequestService.previewConsentRequest(
              props.consentRequestId
          )
    previewHtml.value = html
}

const {
    fetch: fetchPreview,
    isFetching: isPreviewing,
    hasError: hasPreviewError,
    error: previewError,
} = useFetchWrapper(previewInvitationFn)

watch([hasSendError, hasPreviewError], () => {
    if (hasSendError.value || hasPreviewError.value) {
        previewHtml.value = null
    }
})

// ================================
// COMPUTED
// ================================

/** Message d'erreur à afficher (send ou preview) */
const getErrorMessage = computed(() => {
    const err = sendError.value || previewError.value
    if (!err) return ""
    const withResponse = err as Error & {
        response?: { data?: { message?: string }; status?: number }
    }
    return (
        withResponse?.response?.data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

const hasError = computed(() => hasSendError.value || hasPreviewError.value)

/** Lien à copier : pour SHARE on ajoute ?action=accepted */
const getCopyLink = computed(() =>
    props.consentRequestType === CONSENT_REQUEST_TYPE.SHARE && props.link
        ? `${props.link}?action=accepted`
        : (props.link ?? "")
)

// ================================
// HANDLERS
// ================================

function onPreview(): void {
    fetchPreview()
}

function onSend(): void {
    fetchSend()
}

async function onClickCopy(): Promise<void> {
    if (!getCopyLink.value) return
    await copy(getCopyLink.value)
    copyDone.value = true
    setTimeout(() => {
        copyDone.value = false
    }, 1000)
}

/** Fermeture du formulaire (pas de retour en arrière), aligné sur SourceInvitationSignSent */
function onQuit(): void {
    emit("done")
}
</script>

<template>
    <div
        class="source-ask-sharing-sent flex flex-col items-center gap-4 w-full"
    >
        <!-- En-tête -->
        <header class="w-full flex flex-col gap-2 text-center">
            <h2
                class="text-lg font-semibold text-gray-800 flex items-center justify-center gap-2"
            >
                {{ t("title") }}
                <BaseIcon icon="pi pi-check" class="text-green-600" />
            </h2>
            <p v-if="!sendBack" class="text-sm text-gray-600">
                {{ t("help") }}
            </p>
            <p class="text-sm text-gray-600">
                {{ t("notif") }}
            </p>
        </header>

        <!-- Boutons : aperçu, copier, envoyer -->
        <div
            v-if="!successMessage"
            class="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center justify-items-center py-6 w-full"
        >
            <BaseButton
                :label="t('see')"
                severity="secondary"
                :loading="isPreviewing"
                :disabled="isPreviewing"
                class="w-full sm:justify-self-end"
                @click="onPreview"
            />
            <BaseButton
                :label="t('copy')"
                outlined
                :disabled="!getCopyLink"
                class="w-full sm:w-auto gap-2"
                @click="onClickCopy"
            >
                <BaseIcon
                    :icon="copyDone ? 'pi pi-check' : 'pi pi-copy'"
                    :class="copyDone ? 'text-green-600' : ''"
                />
            </BaseButton>
            <BaseButton
                :label="t('send')"
                :loading="isSending"
                :disabled="isSending"
                class="w-full sm:justify-self-start"
                @click="onSend"
            />
        </div>

        <!-- Message de succès -->
        <BaseMessage
            v-if="successMessage"
            severity="success"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ successMessage }}
        </BaseMessage>

        <!-- Message d'erreur -->
        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ getErrorMessage }}
        </BaseMessage>

        <!-- Aperçu du mail -->
        <div
            v-if="previewHtml"
            class="w-full relative rounded border border-gray-200 overflow-hidden"
        >
            <iframe
                :srcdoc="previewHtml"
                title="Aperçu du mail"
                class="w-full border-0 min-h-[200px] pointer-events-none"
                scrolling="no"
                @load="
                    (e: Event) => {
                        const iframe = e.target as HTMLIFrameElement
                        if (iframe?.contentWindow?.document?.body) {
                            iframe.style.height =
                                iframe.contentWindow.document.body
                                    .scrollHeight + 'px'
                        }
                    }
                "
            />
        </div>

        <!-- Terminer (ferme le formulaire, comme SourceInvitationSignSent) -->
        <div class="w-full flex justify-center">
            <BaseButton
                :label="t('quit')"
                class="max-w-[15rem] w-full"
                @click="onQuit"
                severity="secondary"
            />
        </div>
    </div>
</template>
